<template>
  <div
    style="display: contents;"
    class="form-submission-row p-relative"
    @click="handleClick"
  >
    <tr>
      <td colspan="1" @click="$emit('click')">
        <span class="font-weight-bold">
          {{ isPresent(field) ? field.label : group.title }}
        </span>
      </td>
      <td colspan="1">
        <small v-if="allowToEdit" class="text-grey-50 edit-button">
          {{ $t("components.formSubmissionRow.edit") }}
        </small>
      </td>
    </tr>
    <tr>
      <td class="d-flex align-items-center" colspan="1">
        <template v-if="isPresent(group)">
          <div class="w-100 pb-1">
            <form-submission-group :group="group" :values="values" />
          </div>
        </template>

        <template v-if="isPresent(field)">
          <div class="w-100 pb-1">
            <form-submission-field-value
              :field="field"
              :values="values"
              :is-missable="isMissable"
            />
          </div>
        </template>
      </td>
      <td colspan="1"></td>

      <v-modal
        v-if="isEditing && currentGroup"
        @close="() => (isEditing = false)"
      >
        <template :slot="FORM_GROUP_MODAL">
          <form-template
            :values="values"
            :groups="[currentGroup]"
            :is-missable="isMissable"
            @close="close"
            @input="handleUpdate"
          />
        </template>
      </v-modal>

      <!-- TODO: Remove, I hate this being here -->
      <v-modal v-if="exercise" @close="() => (exercise = null)">
        <template :slot="hash">
          <span>
            <exercise-template
              :id="exercise.id"
              :title="exercise.title"
              :description="exercise.description"
              :banner="exercise.banner"
              :video="exercise.video"
              hide-back
            />
          </span>
        </template>
      </v-modal>
    </tr>
    <tr v-if="isAssessment">
      <td colspan="2">
        <div class="d-flex justify-content-between pr-2">
          <div v-if="isCorrectAnswer" class="answer-marker --correct">
            <v-icon icon="check_circle" />
            {{ $t("components.formSubmissionRow.correct") }}
          </div>
          <div v-else class="answer-marker --incorrect">
            <v-icon icon="cancel" />
            {{ $t("components.formSubmissionRow.incorrect") }}
          </div>
          <small
            v-if="!isCorrectAnswer"
            class="text-grey-50 view-answer-button"
          >
            {{ $t("components.formSubmissionRow.viewCorrectAnswer") }}
          </small>
        </div>
      </td>
    </tr>

    <alert-dialog
      v-if="isAssessment"
      :title="$t('components.formSubmissionRow.theCorrectAnswerIs')"
      :show="showCorrectAnswer"
      :actions="[
        {
          title: $t('components.formSubmissionRow.done'),
          callback: () => (showCorrectAnswer = false)
        }
      ]"
      @close="() => (showCorrectAnswer = false)"
    >
      {{ correctAnswer }}
    </alert-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex"

import { isPresent } from "@/lib/utils"
import { addToHash, removeFromHash } from "@/lib/url-helpers"

import FormTemplate from "@/components/page-templates/FormTemplate"
import FormSubmissionGroup from "@/components/form-submission/RowGroup"
import FormSubmissionFieldValue from "@/components/form-submission/FieldValue"

const FORM_GROUP_MODAL = "#form-group"

export default {
  components: {
    FormTemplate,
    FormSubmissionGroup,
    FormSubmissionFieldValue,
    ExerciseTemplate: () =>
      import("@/components/page-templates/ExerciseTemplate"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  model: {
    prop: "values",
    event: "input"
  },

  props: {
    values: { type: Object, required: true },
    field: { type: Object, default: () => null },
    fieldGroup: { type: Object, default: () => null },
    group: { type: Object, default: () => null }, // TODO: requiredIf
    isMissable: { type: Boolean, default: false }
  },

  data() {
    return {
      isEditing: false,
      isLoadingExercise: false,
      exercise: null,
      showCorrectAnswer: false,
      FORM_GROUP_MODAL
    }
  },

  computed: {
    hash() {
      return `#exercise-${this._uid}`
    },

    currentGroup() {
      return this.group
        ? this.group
        : this.fieldGroup
        ? { ...this.fieldGroup, form_fields: [this.field] }
        : null
    },

    allowToEdit() {
      return this.currentGroup && !this.isAssessment
    },

    isCorrectAnswer() {
      return (
        this.isAssessment &&
        this.values[this.field?.id] &&
        this.correctAnswer === this.values[this.field?.id].value
      )
    },

    correctAnswer() {
      return this.field ? this.field.correct_answer : null
    },

    isAssessment() {
      return isPresent(this.correctAnswer)
    }
  },

  methods: {
    isPresent,

    ...mapActions("forms", ["fetchExerciseByItemUid"]),

    handleClick() {
      if (this.isAssessment && !this.isCorrectAnswer) {
        this.showCorrectAnswer = true
      } else {
        this.edit()
      }
    },

    edit() {
      if (!this.allowToEdit) return

      this.isEditing = true
      this.currentValue = this.value

      this.$router.push(addToHash(this.$route.hash, FORM_GROUP_MODAL))
    },

    handleUpdate(values) {
      this.$emit("input", values)

      this.close()
    },

    close() {
      this.isEditing = false
      this.$router.push(removeFromHash(this.$route.hash, FORM_GROUP_MODAL))
    },

    showExerciseByGroup(group) {
      if (group.uid && group.uid.includes("WorkoutGroupItem")) {
        const k = group.uid.split("-")
        const workoutGroupItemId = parseInt(k[k.length - 1])

        if (isNaN(workoutGroupItemId)) {
          return
        }

        this.isLoadingExercise = true

        this.fetchExerciseByItemUid(workoutGroupItemId)
          .then(exercise => {
            if (exercise && !(this._isBeingDestroyed || this._isDestroyed)) {
              this.exercise = exercise

              this.$router.push(addToHash(this.$route.hash, this.hash))
            }
          })
          .finally(() => {
            this.isLoadingExercise = false
          })
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.form-submission-row {
  cursor: pointer;

  &:hover {
    tr {
      background-color: $color-ui--grey-10;
    }
  }

  &:not(:last-child) {
    > tr:last-child {
      border-bottom: 1px solid $color-ui--grey-20;
    }
  }

  > tr:first-child {
    > td:last-child {
      display: flex;
      justify-content: flex-end;
      padding-right: $size--4;
    }
  }

  > tr:last-child {
    > td {
      padding-top: 0;
    }
  }

  tr {
    transition: background-color 0.3s;

    &:first-child {
      td {
        padding-top: $size--4;
      }
    }

    td {
      padding-bottom: $size--2;
      vertical-align: middle;
    }

    > td {
      padding-left: $size--4;
    }

    .edit-button,
    .view-answer-button {
      font-size: 12px;
      cursor: pointer;
    }
  }

  .answer-marker {
    display: flex;
    align-items: center;
    font-size: $font-size--small;
    font-weight: 600;
    margin-bottom: $size--2;

    &.--correct {
      color: $color-ui--green-dot;
    }

    &.--incorrect {
      color: $color-ui--red;
    }

    .v-icon {
      margin-right: $size--2;
      font-size: $font-size--large;
    }
  }
}
</style>
